// Ajustes › Lixeira (0.65): o que foi excluído nos últimos 30 dias, em todas as abas. Restaurar ou excluir de vez.
// 0.67 (texto 05 C3): filtro por aba e senha para "Excluir de vez" e "Esvaziar a lixeira" quando há itens grandes
// (fichário, paciente, viagem, turma...). Do mais recente para o mais antigo.
import { useState } from 'react';
import { Alert, Pressable, Text, View } from 'react-native';
import { useDoc } from '../../store/store';
import type { AbaId, ItemLixeira } from '../../store/types';
import { diasRestantes, esvaziarLixeira, excluirDeVez, podeRestaurar, restaurarDaLixeira } from '../../lib/lixeira';
import { useConfirmarIdentidade } from '../../components/ConfirmarIdentidade';
import { ActionSheet } from '../../components/ui';
import { aba, C } from '../../components/theme';
import { brDateTime } from '../../lib/date';
import { t } from '../../lib/i18n';
import { Card } from './comum';

/** Item grande: tem muita coisa dentro (fichário, paciente, viagem, turma, cartão com compras...). */
export function ehGrandeNaLixeira(x: ItemLixeira) {
  if (x.extra?.grande) return true;
  if (x.tipo === 'estudos' && x.extra?.tipo === 'fichario') return true;
  if (x.tipo === 'reg' && Array.isArray(x.dados) && x.dados.length > 1) return true;   // ficha com o que estava dentro
  if (x.tipo === 'financas') return true;
  if (x.tipo === 'lista' && x.extra?.doc === 'financas' && x.extra?.campo === 'metas') return true;   // meta (item grande em Finanças)
  return false;
}
/** Paciente e prontuário (Consultório, Saúde): mesmas regras de privacidade de hoje, também na Lixeira. */
export const ehSigilosoNaLixeira = (x: ItemLixeira) => !!x.extra?.sigilo || (x.tipo === 'reg' && ['consultorio', 'saude'].includes(x.extra?.mod));
/** Título mostrado: nas fichas protegidas antigas (sem o título neutro) não mostra o que foi gravado. */
const tituloDe = (x: ItemLixeira) => (ehSigilosoNaLixeira(x) && !x.extra?.sigilo ? '🔒 ' + t('Ficha protegida') : x.titulo);
const nomeAba = (id: AbaId) => { try { const a = aba(id); return `${a.icon} ${a.nome}`; } catch { return id; } };

export default function Lixeira({ cor }: { cor: string }) {
  const itens = useDoc('lixeira').itens ?? [];
  const ident = useConfirmarIdentidade(cor);
  const [filtro, setFiltro] = useState<AbaId | null>(null);
  const [menu, setMenu] = useState(false);
  const lim = Date.now() - 30 * 864e5;
  const todos = itens.filter(x => new Date(x.em).getTime() >= lim).sort((a, b) => b.em.localeCompare(a.em));
  const abas = [...new Set(todos.map(x => x.aba))];
  const lista = filtro ? todos.filter(x => x.aba === filtro) : todos;

  const textoSigilo = t('Dados de paciente e prontuário. Confirme com a senha do app, a biometria ou o código do dispositivo.');
  const excluir = (x: ItemLixeira) => Alert.alert(t('Excluir de vez?'), tituloDe(x), [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: async () => {
    if ((ehGrandeNaLixeira(x) || ehSigilosoNaLixeira(x)) && !(await ident.pedir(t('Excluir de vez'), { texto: ehSigilosoNaLixeira(x) ? textoSigilo : t('Este item tem muita coisa dentro. Confirme com a senha do app, a biometria ou o código do dispositivo.'), botao: t('Excluir'), perigo: true }))) return;
    excluirDeVez(x.id);
  } }]);
  const restaurar = async (x: ItemLixeira) => {
    if (ehSigilosoNaLixeira(x) && !(await ident.pedir(t('Restaurar'), { texto: textoSigilo, botao: t('Restaurar') }))) return;
    if (restaurarDaLixeira(x.id)) Alert.alert(t('Restaurado'), t('"{nome}" voltou para o lugar.', { nome: tituloDe(x) }));
    else if (x.aba === 'diario') Alert.alert(t('Abra o Diário primeiro'), t('O que sai do Diário fica cifrado na lixeira. Entre no Diário e depois volte aqui para restaurar.'));
  };
  const esvaziar = () => Alert.alert(t('Esvaziar a lixeira?'), t('Os itens saem de vez e não podem mais ser restaurados.'), [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Esvaziar'), style: 'destructive', onPress: async () => {
    if (todos.some(x => ehGrandeNaLixeira(x) || ehSigilosoNaLixeira(x)) && !(await ident.pedir(t('Esvaziar a lixeira'), { texto: t('A lixeira tem itens grandes. Confirme com a senha do app, a biometria ou o código do dispositivo.'), botao: t('Esvaziar'), perigo: true }))) return;
    esvaziarLixeira();
  } }]);

  return (<>
    <Card>
      <Text style={{ color: C.sub, fontSize: 13 }}>{t('Tudo o que você exclui fica aqui por 30 dias. Depois disso sai de vez.')}</Text>
      {abas.length > 1 && (
        <Pressable onPress={() => setMenu(true)} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 6 }}>
          <Text style={{ fontSize: 16 }}>🔎</Text>
          <Text style={{ color: C.text, flex: 1, fontWeight: '600' }}>{t('Aba')}</Text>
          <Text style={{ color: C.sub, fontSize: 13 }}>{filtro ? nomeAba(filtro) : t('Todas')}</Text>
          <Text style={{ color: C.mute, fontSize: 18 }}>›</Text>
        </Pressable>
      )}
      {!!todos.length && (
        <Pressable onPress={esvaziar}>
          <Text style={{ color: C.danger, fontWeight: '700' }}>🗑 {t('Esvaziar a lixeira')}</Text>
        </Pressable>
      )}
    </Card>
    {!lista.length && <Text style={{ color: C.sub }}>{t('A lixeira está vazia.')}</Text>}
    {lista.map(x => (
      <View key={x.id} style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 6 }}>
        <Text style={{ color: C.text, fontWeight: '700' }} numberOfLines={2}>{tituloDe(x)}</Text>
        <Text style={{ color: C.sub, fontSize: 12 }}>{[nomeAba(x.aba), t('excluído em {d}', { d: brDateTime(x.em) }), t('sai de vez em {n} dia(s)', { n: diasRestantes(x) })].filter(Boolean).join(' · ')}</Text>
        <View style={{ flexDirection: 'row', gap: 18, justifyContent: 'flex-end' }}>
          {podeRestaurar(x)
            ? <Pressable onPress={() => restaurar(x)} hitSlop={6}><Text style={{ color: cor, fontWeight: '800' }}>{ehSigilosoNaLixeira(x) ? '🔒 ' : ''}↩︎ {t('Restaurar')}</Text></Pressable>
            : <Text style={{ color: C.mute, fontSize: 12 }}>{t('Abra a aba para poder restaurar')}</Text>}
          <Pressable onPress={() => excluir(x)} hitSlop={6}>
            <Text style={{ color: C.danger }}>{ehGrandeNaLixeira(x) || ehSigilosoNaLixeira(x) ? '🔒 ' : ''}{t('Excluir de vez')}</Text>
          </Pressable>
        </View>
      </View>
    ))}
    <ActionSheet title={t('Aba')} actions={menu ? [
      { label: (filtro ? '    ' : '✓ ') + t('Todas'), onPress: () => setFiltro(null) },
      ...abas.map(a => ({ label: (filtro === a ? '✓ ' : '    ') + nomeAba(a), onPress: () => setFiltro(a) })),
    ] : null} onClose={() => setMenu(false)} />
    {ident.tela}
  </>);
}
